/**
 * Schema-driven sync API framework.
 *
 * Define your entities once with `array()`, `collection()`, `value()`, or `custom()`.
 * Call `createSyncApi(schema)` to get a fully typed object with:
 *   - `.use()` — reactive Zustand hook
 *   - `.get()` — snapshot read
 *   - `.set()` / `.add()` / `.update()` / `.remove()` — typed mutations
 *
 * Types are inferred end-to-end from the schema definition.
 */

import type { LWWItem } from '@flashka/shared/sync-engine/framework/define-sync-entity';

import { useShallow } from 'zustand/react/shallow';

import { useFlashkaStore } from '../../internal/store/flashka-store';

// ---------------------------------------------------------------------------
// Entity definition types (schema descriptors)
// ---------------------------------------------------------------------------

export const ENTITY_KIND = Symbol.for('sync-entity-kind');

/**
 * Mutation-only surface passed to extension factories for array/collection entities.
 * Deliberately omits `use`/`useFind` — those are React hooks and shouldn't be
 * called from event-handler-style extension bodies.
 */
export type ArrayEntityOps<T> = {
  /** Snapshot read. */
  get(): T[];
  /** Replace all items. */
  set(data: T[]): void;
  /** Add a single item. */
  add(item: T): void;
  /** Update an item by primary key (partial merge). */
  update(id: string, partial: Partial<T>): void;
  /** Remove an item by primary key. */
  remove(id: string): void;
};

/** Mutation-only surface for record entities. */
export type RecordEntityOps<TEntry> = {
  get(): Record<string, TEntry>;
  set(entry: TEntry): void;
  remove(entryKey: string): void;
};

/**
 * Extension factory. Called once at `createSyncApi()` time.
 *
 * - `ops` — the clean mutation surface (preferred). All mutations here go
 *   through the framework's auto-generated CRUD, so they auto-stamp
 *   `updatedAtISO`, persist to IndexedDB, and push to the server.
 * - `self` — the full entity API (including reactive `use`/`useFind` hooks).
 *   Use only when `ops` is not enough; avoid calling hooks from non-render
 *   contexts. Does NOT include the extensions themselves (to avoid a
 *   circular type).
 */
export type ExtensionFactory<TOps, TSelf, Ext> = (ops: TOps, self: TSelf) => Ext;

type AnyExt = Record<string, (...args: any[]) => any>;

export type ArrayEntityDef<T, Ext extends AnyExt = Record<never, never>> = {
  readonly [ENTITY_KIND]: 'array';
  /** @internal phantom — carries the item type for inference */
  readonly _type?: T;
  /** @internal phantom — carries extension types */
  readonly _ext?: Ext;
  readonly config: {
    key: string;
    primaryKey: string;
    extensions?: ExtensionFactory<ArrayEntityOps<T>, ArrayEntityApi<T>, Ext>;
  };
};

/**
 * A collection entity tracks items individually with per-item LWW timestamps.
 * Unlike `array` (whole-blob LWW), each item carries `updatedAtISO` and
 * only changed items are synced. Supports soft deletes via `deletedTimeISO`.
 *
 * Items must extend `{ updatedAtISO?: string; deletedTimeISO?: string | null }`.
 */
export type CollectionEntityDef<T, Ext extends AnyExt = Record<never, never>> = {
  readonly [ENTITY_KIND]: 'collection';
  readonly _type?: T;
  readonly _ext?: Ext;
  readonly config: {
    key: string;
    primaryKey: string;
    extensions?: ExtensionFactory<ArrayEntityOps<T>, ArrayEntityApi<T>, Ext>;
  };
};

export type ValueEntityDef<T> = {
  readonly [ENTITY_KIND]: 'value';
  readonly _type?: T;
  readonly config: {
    key: string;
  };
};

export type RecordEntityDef<TEntry, Ext extends AnyExt = Record<never, never>> = {
  readonly [ENTITY_KIND]: 'record';
  readonly _type?: TEntry;
  readonly _ext?: Ext;
  readonly config: {
    key: string;
    extensions?: ExtensionFactory<RecordEntityOps<TEntry>, RecordEntityApi<TEntry>, Ext>;
  };
};

type CustomEntityDef<TApi> = {
  readonly [ENTITY_KIND]: 'custom';
  readonly api: TApi;
};

/**
 * Union of every entity-def variant — used only as the constraint for
 * `createSyncApi<Schema extends Record<string, EntityDef>>`. T / Ext are
 * erased here: concrete types are recovered via conditional inference in
 * `InferApi<D>`. `unknown` is too narrow for the Ext record constraint, so
 * we keep the `any`s confined to this one line.
 */
type EntityDef =
  | ArrayEntityDef<any, any>
  | CollectionEntityDef<any, any>
  | RecordEntityDef<any, any>
  | ValueEntityDef<unknown>
  | CustomEntityDef<unknown>;

// ---------------------------------------------------------------------------
// API types (what components consume)
// ---------------------------------------------------------------------------

export type ArrayEntityApi<T> = {
  /** Reactive hook — subscribes to Zustand, re-renders on change. */
  use(): T[];
  /** Reactive hook with selector — only re-renders when the derived value changes. */
  use<R>(selector: (data: T[]) => R): R;
  /** Find a single item by primary key (reactive). */
  useFind(id: string): T | undefined;
  /** Snapshot — reads current state without subscribing. */
  get(): T[];
  /** Replace all items. */
  set(data: T[]): void;
  /** Add a single item. */
  add(item: T): void;
  /** Update an item by primary key. */
  update(id: string, partial: Partial<T>): void;
  /** Remove an item by primary key. */
  remove(id: string): void;
};

export type ValueEntityApi<T> = {
  use(): T;
  use<R>(selector: (data: T) => R): R;
  get(): T;
  set(data: T): void;
};

export type RecordEntityApi<TEntry> = {
  /** All entries (reactive). */
  use(): Record<string, TEntry>;
  /** Derived value from entries (reactive, re-renders only when result changes). */
  use<R>(selector: (data: Record<string, TEntry>) => R): R;
  /** All entries (snapshot). */
  get(): Record<string, TEntry>;
  /** Upsert an entry. */
  set(entry: TEntry): void;
  /** Remove an entry by key. */
  remove(key: string): void;
};

// ---------------------------------------------------------------------------
// Mapped type: schema → API
// ---------------------------------------------------------------------------

type InferApi<D> =
  D extends ArrayEntityDef<infer T, infer Ext>
    ? ArrayEntityApi<T> & Ext
    : D extends CollectionEntityDef<infer T, infer Ext>
      ? ArrayEntityApi<T> & Ext
      : D extends RecordEntityDef<infer TEntry, infer Ext>
        ? RecordEntityApi<TEntry> & Ext
        : D extends ValueEntityDef<infer T>
          ? ValueEntityApi<T>
          : D extends CustomEntityDef<infer TApi>
            ? TApi
            : never;

export type SyncApi<Schema extends Record<string, EntityDef>> = {
  [K in keyof Schema]: InferApi<Schema[K]>;
};

// ---------------------------------------------------------------------------
// Helper functions (used in schema definition)
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function singularize(name: string): string {
  const cap = capitalize(name);
  return cap.endsWith('s') ? cap.slice(0, -1) : cap;
}

/**
 * Define an array entity (collection with ID-based CRUD).
 *
 * Curried form `array<T>()({...})` is required because TypeScript doesn't
 * support partial type argument inference — the outer call pins `T`
 * explicitly while the inner call infers `Ext` from the extensions arg.
 *
 * Extensions are declared as a **factory** `(ops, self) => ({...})`. `ops`
 * is the clean mutation surface (`get/set/add/update/remove`); `self` is
 * the full entity API (use/useFind/get/...). Mutations inside extensions
 * go through `ops` and automatically persist + sync.
 *
 * ```ts
 * folders: array<SyncFolder>()({
 *   key: "folders",
 *   primaryKey: "id",
 *   extensions: (ops) => ({
 *     moveToParent: (id, parentId) => ops.update(id, { parentFolder: parentId }),
 *   }),
 * })
 * ```
 */
export function array<T>() {
  // Overloads: without extensions → Ext defaults cleanly to Record<never, never>.
  // Without overloads, omitting `extensions` causes TypeScript to mis-infer Ext
  // and collapse the entire entity API to `any`.
  function define<Ext extends AnyExt>(config: {
    key: string;
    primaryKey: string;
    extensions: ExtensionFactory<ArrayEntityOps<T>, ArrayEntityApi<T>, Ext>;
  }): ArrayEntityDef<T, Ext>;
  function define(config: { key: string; primaryKey: string }): ArrayEntityDef<T>;
  function define(config: Record<string, unknown>) {
    return { [ENTITY_KIND]: 'array', config } as ArrayEntityDef<T>;
  }
  return define;
}

/**
 * Define a collection entity — like `array`, but with **per-item LWW conflict
 * resolution** instead of whole-blob LWW. Each item is individually timestamped,
 * only changed items are synced, and concurrent edits to different items merge
 * without conflict.
 *
 * Items must have `updatedAtISO?: string` and `deletedTimeISO?: string | null`.
 * The framework auto-stamps `updatedAtISO` on mutations and uses soft deletes.
 * `use()` automatically filters out soft-deleted items.
 *
 * Curried form `collection<T>()({...})` — see `array()` for why.
 *
 * ```ts
 * folders: collection<SyncFolder>()({ key: "folders", primaryKey: "id" })
 * ```
 */
export function collection<
  T extends { updatedAtISO?: string; deletedTimeISO?: string | null },
>() {
  function define<Ext extends AnyExt>(config: {
    key: string;
    primaryKey: string;
    extensions: ExtensionFactory<ArrayEntityOps<T>, ArrayEntityApi<T>, Ext>;
  }): CollectionEntityDef<T, Ext>;
  function define(config: { key: string; primaryKey: string }): CollectionEntityDef<T>;
  function define(config: Record<string, unknown>) {
    return { [ENTITY_KIND]: 'collection', config } as CollectionEntityDef<T>;
  }
  return define;
}

/**
 * Define a value entity (single LWW value).
 *
 * ```ts
 * settings: value<SpacedRepetitionSettings>({ key: "globalSrSettings" })
 * ```
 */
export function value<T>(config: { key: string }): ValueEntityDef<T> {
  return { [ENTITY_KIND]: 'value', config } as ValueEntityDef<T>;
}

/**
 * Define a record entity — a `Record<string, TEntry>` where each entry is
 * independently timestamped and synced. Each entry has `set` and `remove`.
 *
 * Curried form `record<T>()({...})` — see `array()` for why.
 *
 * ```ts
 * quizzes: record<SyncQuizProgressEntry>()({ key: "quizProgress" })
 * ```
 */
export function record<TEntry>() {
  function define<Ext extends AnyExt>(config: {
    key: string;
    extensions: ExtensionFactory<RecordEntityOps<TEntry>, RecordEntityApi<TEntry>, Ext>;
  }): RecordEntityDef<TEntry, Ext>;
  function define(config: { key: string }): RecordEntityDef<TEntry>;
  function define(config: Record<string, unknown>) {
    return { [ENTITY_KIND]: 'record', config } as RecordEntityDef<TEntry>;
  }
  return define;
}

/**
 * Define a custom entity with a hand-written API (for complex entities like deck/quiz).
 *
 * ```ts
 * decks: custom<DeckApi>({ use: () => ..., updateCards: () => ... })
 * ```
 */
export function custom<TApi>(api: TApi): CustomEntityDef<TApi> {
  return { [ENTITY_KIND]: 'custom', api } as CustomEntityDef<TApi>;
}

// ---------------------------------------------------------------------------
// Factory helpers — centralise the `as any` casts in three places
// ---------------------------------------------------------------------------

/**
 * A dynamically-keyed view of the Zustand store. Entity names and method
 * names are computed at runtime from the schema, so static typing of each
 * key is impossible at this layer; individual reads cast to the expected type.
 */
type DynamicStore = Record<string, unknown>;

/** Snapshot of the Zustand store (dynamically keyed). */
function storeSnap(): DynamicStore {
  return useFlashkaStore.getState() as unknown as DynamicStore;
}

/**
 * Zustand hook that reads `store[key]`, optionally through a user selector.
 *
 * Always uses `useShallow` so Zustand can bail out of re-renders when the
 * derived value is structurally the same (shallow comparison). When no
 * selector is provided, shallow still degrades gracefully — for primitives
 * and same-reference objects it's equivalent to `===`.
 */
function useStoreKey<T>(key: string, selector?: (data: T) => unknown): unknown {
  return useFlashkaStore(
    useShallow((s) => {
      const raw = (s as unknown as DynamicStore)[key] as T;
      return selector ? selector(raw) : raw;
    }),
  );
}

/** Call a method on the store snapshot. */
function callStore(method: string, ...args: unknown[]): unknown {
  return (storeSnap()[method] as (...a: unknown[]) => unknown)(...args);
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a typed sync API from a schema definition.
 *
 * Returns an object where each key maps to a typed entity accessor.
 * Array entities get `use/get/set/add/update/remove/useFind` + extensions.
 * Value entities get `use/get/set`.
 * Custom entities get whatever API object was passed in.
 */
export function createSyncApi<S extends Record<string, EntityDef>>(
  schema: S,
): SyncApi<S> {
  const api = {} as Record<string, unknown>;

  for (const [name, def] of Object.entries(schema)) {
    const kind = (def as { [ENTITY_KIND]: string })[ENTITY_KIND];

    if (kind === 'array' || kind === 'collection') {
      const { key, primaryKey, extensions } = (def as ArrayEntityDef<unknown>).config;
      const singular = singularize(key);
      const setMethod = `set${capitalize(key)}`;
      const addMethod = `add${singular}`;
      const updateMethod = `update${singular}`;
      const removeMethod = `remove${singular}`;

      const isCollection = kind === 'collection';

      // Build the base API (what extensions see as `self`).
      const baseApi = {
        // For collections, filter + optional selector are composed into a
        // single Zustand selector wrapped with `useShallow`. Shallow
        // equality compares the result element-wise for arrays (no
        // selector) or via `===` for primitives (with selector), so the
        // component only re-renders when the derived value truly changes —
        // not on every sibling mutation.
        //
        // Trade-off: `.filter()` runs on every store state change (not just
        // when this key changes). For small arrays this is negligible and
        // far cheaper than unnecessary React re-renders.
        use: isCollection
          ? (selector?: (data: unknown[]) => unknown) => {
              return useFlashkaStore(
                useShallow((s) => {
                  const raw = (s as unknown as DynamicStore)[key] as LWWItem[];
                  const filtered = raw.filter((x) => !x.deletedTimeISO);
                  return selector ? selector(filtered) : filtered;
                }),
              );
            }
          : (selector?: (data: unknown[]) => unknown) => useStoreKey(key, selector),
        // useFind pushes the .find() into the Zustand selector so the
        // component only re-renders when the matched item actually changes,
        // not when any sibling in the array changes. `shallow` equality
        // ensures a structurally-identical item (e.g. after hydration)
        // doesn't trigger a re-render.
        useFind: (id: string) => {
          return useFlashkaStore(
            useShallow((s) => {
              const arr = (s as unknown as DynamicStore)[key] as LWWItem[];
              return isCollection
                ? arr.find((x) => !x.deletedTimeISO && x[primaryKey] === id)
                : arr.find((x) => x[primaryKey] === id);
            }),
          );
        },
        get: isCollection
          ? () => (storeSnap()[key] as LWWItem[]).filter((x) => !x.deletedTimeISO)
          : () => storeSnap()[key],
        set: (data: unknown) => callStore(setMethod, data),
        add: (item: unknown) => callStore(addMethod, item),
        update: (id: string, partial: unknown) => callStore(updateMethod, id, partial),
        remove: (id: string) => callStore(removeMethod, id),
      };

      // `ops` is the mutation subset — same references as `baseApi`, no hooks.
      const ops = {
        get: baseApi.get,
        set: baseApi.set,
        add: baseApi.add,
        update: baseApi.update,
        remove: baseApi.remove,
      };

      const ext =
        typeof extensions === 'function'
          ? (extensions as (o: unknown, s: unknown) => Record<string, unknown>)(
              ops,
              baseApi,
            )
          : {};

      api[name] = { ...baseApi, ...ext };
    } else if (kind === 'record') {
      const { key, extensions } = (def as RecordEntityDef<unknown>).config;
      const setEntryMethod = `setEntry${capitalize(key)}`;
      const removeEntryMethod = `removeEntry${capitalize(key)}`;

      const baseApi = {
        use: (selector?: (data: unknown) => unknown) => useStoreKey(key, selector),
        get: () => storeSnap()[key],
        set: (entry: unknown) => callStore(setEntryMethod, entry),
        remove: (entryKey: string) => callStore(removeEntryMethod, entryKey),
      };

      const ops = {
        get: baseApi.get,
        set: baseApi.set,
        remove: baseApi.remove,
      };

      const ext =
        typeof extensions === 'function'
          ? (extensions as (o: unknown, s: unknown) => Record<string, unknown>)(
              ops,
              baseApi,
            )
          : {};

      api[name] = { ...baseApi, ...ext };
    } else if (kind === 'value') {
      const { key } = (def as ValueEntityDef<unknown>).config;
      const setMethod = `set${capitalize(key)}`;

      api[name] = {
        use: (selector?: (data: unknown) => unknown) => useStoreKey(key, selector),
        get: () => storeSnap()[key],
        set: (data: unknown) => callStore(setMethod, data),
      };
    } else if (kind === 'custom') {
      api[name] = (def as CustomEntityDef<unknown>).api;
    }
  }

  return api as SyncApi<S>;
}
